import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CleaningServices,
  DirtyLens,
  Engineering,
  DoNotDisturb,
  Hotel,
  TransferWithinAStation,
  Layers,
  Search,
  ExpandMore,
} from '@mui/icons-material';
import API from '../api/axios';
import RoomCard from '../components/RoomCard';

function Dashboard({ user, stompClient, showToast }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const fetchRooms = useCallback(async () => {
    try {
      const response = await API.get('/rooms');
      setRooms(response.data);
    } catch (err) {
      console.error('Error fetching rooms:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    if (!stompClient || !stompClient.connected) return;

    const subscription = stompClient.subscribe('/topic/rooms', (message) => {
      const updatedRoom = JSON.parse(message.body);
      setRooms((prev) =>
          prev.map((room) =>
              room.id === updatedRoom.id ? updatedRoom : room
          )
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [stompClient]);

  const statusConfig = {
    CLEAN: { color: '#2e7d32', bg: '#e8f5e9', icon: <CleaningServices />, label: 'Curată' },
    DIRTY: { color: '#c62828', bg: '#ffebee', icon: <DirtyLens />, label: 'Murdară' },
    IN_PROGRESS: { color: '#ef6c00', bg: '#fff3e0', icon: <Engineering />, label: 'În curs' },
    OUT_OF_ORDER: { color: '#616161', bg: '#f5f5f5', icon: <DoNotDisturb />, label: 'Indisponibilă' },
    OCCUPIED: { color: '#1565c0', bg: '#e3f2fd', icon: <Hotel />, label: 'Ocupată' },
    IN_TRANSIT: { color: '#7b1fa2', bg: '#f3e5f5', icon: <TransferWithinAStation />, label: 'În tranzit' },
  };

  const filteredRooms = rooms
      .filter((room) => filter === 'ALL' || room.status === filter)
      .filter((room) =>
          search === '' ||
          room.roomNumber.toLowerCase().includes(search.toLowerCase()) ||
          room.roomType.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        if (a.floor !== b.floor) return a.floor - b.floor;
        return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      });

  const statusCounts = rooms.reduce((acc, room) => {
    acc[room.status] = (acc[room.status] || 0) + 1;
    return acc;
  }, {});

  // Group by floor
  const floors = [...new Set(filteredRooms.map((r) => r.floor))].sort((a, b) => a - b);

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
    );
  }

  return (
      <Box>
        {/* Stats Overview */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(statusConfig).map(([status, config]) => (
              <Grid item xs={6} sm={3} key={status}>
                <Paper
                    sx={{
                      p: 2.5,
                      textAlign: 'center',
                      borderRadius: 3,
                      background: config.bg,
                      border: `2px solid ${filter === status ? config.color : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 20px ${config.color}30`,
                      },
                    }}
                    onClick={() => setFilter(filter === status ? 'ALL' : status)}
                >
                  <Box sx={{ color: config.color, mb: 1 }}>{config.icon}</Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: config.color }}>
                    {statusCounts[status] || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: config.color, fontWeight: 500 }}>
                    {config.label}
                  </Typography>
                </Paper>
              </Grid>
          ))}
        </Grid>

        {/* Search + Filter Bar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <TextField
              size="small"
              placeholder="Caută cameră (nr, tip)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                ),
              }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
                label={`Toate (${rooms.length})`}
                onClick={() => setFilter('ALL')}
                variant={filter === 'ALL' ? 'filled' : 'outlined'}
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
            />
            {Object.entries(statusConfig).map(([status, config]) => (
                <Chip
                    key={status}
                    label={`${config.label} (${statusCounts[status] || 0})`}
                    onClick={() => setFilter(filter === status ? 'ALL' : status)}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: filter === status ? config.color : config.bg,
                      color: filter === status ? '#fff' : config.color,
                      border: `1px solid ${config.color}40`,
                    }}
                />
            ))}
          </Box>
        </Paper>
        {/* Rooms grouped by floor */}
        {floors.map((floor) => {
          const floorRooms = filteredRooms.filter((r) => r.floor === floor);
          const floorStatusCounts = floorRooms.reduce((acc, r) => {
            acc[r.status] = (acc[r.status] || 0) + 1;
            return acc;
          }, {});

          return (
              <Accordion
                  key={floor}
                  defaultExpanded
                  sx={{
                    mb: 2,
                    borderRadius: '12px !important',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                  }}
              >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 },
                    }}
                >
                  <Layers sx={{ color: '#1a237e' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Etaj {floor}
                  </Typography>
                  <Chip label={`${floorRooms.length} camere`} size="small" sx={{ ml: 1 }} />
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', mr: 2 }}>
                    {Object.entries(statusConfig).map(([status, config]) => {
                      const count = floorStatusCounts[status] || 0;
                      if (count === 0) return null;
                      return (
                          <Chip
                              key={status}
                              label={`${count} ${config.label}`}
                              size="small"
                              sx={{
                                backgroundColor: config.bg,
                                color: config.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                          />
                      );
                    })}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {floorRooms.map((room) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={room.id}>
                          <RoomCard
                              room={room}
                              user={user}
                              statusConfig={statusConfig}
                              onUpdate={fetchRooms}
                              showToast={showToast}
                          />
                        </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
          );
        })}

        {filteredRooms.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {search ? `Nu s-au găsit camere pentru "${search}"` : 'Nu sunt camere'}
              </Typography>
            </Box>
        )}
      </Box>
  );
}

export default Dashboard;